import { useEffect, useId, useRef } from 'react'

import { createRoot } from 'react-dom/client'

import styles from './BotVerificationDialog.module.scss'

import { BotVerificationConfigurationResponse } from '../../../apis/fvillage/api'
import i18n from '../../../i18n'

export type Turnstile = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string
      action: string
      cData: string
      callback: (token: string) => void
      'error-callback': () => void
      'expired-callback': () => void
    }
  ) => string
  remove: (widgetId: string) => void
}

const scriptState: { loading?: Promise<Turnstile> } = {}

const loadTurnstile = (): Promise<Turnstile> => {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (scriptState.loading) return scriptState.loading
  scriptState.loading = new Promise<Turnstile>((resolve, reject) => {
    const script = document.createElement('script')
    const state: { timeout?: number } = {}
    const fail = () => {
      window.clearTimeout(state.timeout)
      script.onload = null
      script.onerror = null
      script.remove()
      reject(new Error('Verification could not load'))
    }
    state.timeout = window.setTimeout(fail, 15000)
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.onload = () => {
      window.clearTimeout(state.timeout)
      if (window.turnstile) resolve(window.turnstile)
      else fail()
    }
    script.onerror = fail
    document.head.appendChild(script)
  }).catch((error: unknown) => {
    scriptState.loading = undefined
    throw error
  })
  return scriptState.loading
}

export const BotVerificationDialog = ({
  configuration,
  onComplete,
  onCancel,
}: {
  configuration: BotVerificationConfigurationResponse
  onComplete: (token: string) => void
  onCancel: () => void
}) => {
  const titleId = useId()
  const descriptionId = useId()
  const dialog = useRef<HTMLDialogElement>(null)
  const container = useRef<HTMLDivElement>(null)
  // Synchronize the native dialog and the external verification widget.
  useEffect(() => {
    const nativeDialog = dialog.current
    nativeDialog?.showModal()
    const state: { active: boolean; widget?: string; sdk?: Turnstile } = {
      active: true,
    }
    loadTurnstile()
      .then((turnstile) => {
        if (!state.active || !container.current) return
        state.sdk = turnstile
        state.widget = turnstile.render(container.current, {
          sitekey: configuration.siteKey,
          action: configuration.action,
          cData: configuration.context,
          callback: (token) => state.active && onComplete(token),
          'error-callback': () => state.active && onCancel(),
          'expired-callback': () => state.active && onCancel(),
        })
      })
      .catch(() => state.active && onCancel())
    return () => {
      state.active = false
      nativeDialog?.close()
      if (state.widget !== undefined) state.sdk?.remove(state.widget)
    }
  }, [configuration, onComplete, onCancel])
  return (
    <dialog
      ref={dialog}
      className={styles.dialog}
      onCancel={onCancel}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <h2 id={titleId}>{i18n.t('components.BotVerificationDialog.title')}</h2>
      <p id={descriptionId}>
        {i18n.t('components.BotVerificationDialog.description')}
      </p>
      <div ref={container} />
      <button type="button" onClick={onCancel}>
        {i18n.t('components.BotVerificationDialog.cancel')}
      </button>
    </dialog>
  )
}

export const runBotVerificationDialog = (
  configuration: BotVerificationConfigurationResponse
): Promise<string> =>
  new Promise((resolve, reject) => {
    const container = document.createElement('div')
    document.body.appendChild(container)
    const root = createRoot(container)
    const state: { finished: boolean; timeout?: number } = { finished: false }
    const finish = (token?: string) => {
      if (state.finished) return
      state.finished = true
      window.clearTimeout(state.timeout)
      root.unmount()
      container.remove()
      if (token) resolve(token)
      else reject(new Error('Security verification was not completed'))
    }
    state.timeout = window.setTimeout(() => finish(), 120000)
    root.render(
      <BotVerificationDialog
        configuration={configuration}
        onComplete={(token) => finish(token)}
        onCancel={() => finish()}
      />
    )
  })
